import { EditableProTable } from '@ant-design/pro-components'
import { Button, Form, Popconfirm, Select, Typography, Upload, Modal } from 'antd'
import FormSection from 'components/form-section'
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { generateUuid } from 'utils/dataUtil'
import { isDecreasing, isIncreasing } from 'utils/number'
import { isNumeric } from 'utils/string'
import Papa from 'papaparse'
import { TableOutlined } from '@ant-design/icons';
import SGOTableEditor from './SGOTableEditor';
import { useQuery } from '@tanstack/react-query'
import { getReferenceList, getReferenceData } from 'service/completionOptimizer'

import classes from './Table.module.scss'

const SGOTable = ({ tab, fields = [], operation, isCondensate, isGas }) => {
  const form = Form.useFormInstance()

  const [data, setData] = useState([])
  const [errors, setErrors] = useState([])
  const [isSGOTableEditorOpen, setIsSGOTableEditorOpen] = useState(false);
  const [editingData, setEditingData] = useState([]);
  const tableEditorRef = useRef(null);

  const { data: references } = useQuery({
    queryKey: ['references', tab === 'matrix' ? 'scal' : 'fracture', 'sgo'],
    queryFn: () => {
      console.log(`Fetching ${tab} SGO references list...`)
      return getReferenceList(tab === 'matrix' ? 'scal' : 'fracture', 'sgo').then(data => {
        console.log('SGO Reference List:', {
          group: tab === 'matrix' ? 'scal' : 'fracture',
          type: 'sgo',
          data: data
        })
        return data
      })
    }
  })

  const referenceOptions = useMemo(() => {
    if (!references) return []
    return references.map(ref => ({
      label: ref.name,
      value: ref.id
    }))
  }, [references])

  const handleReferenceChange = async (value) => {
    if (!value) return

    try {
      const group = tab === 'matrix' ? 'scal' : 'fracture'
      console.log('Fetching SGO reference data:', {
        group,
        type: 'sgo',
        id: value
      })
      
      const referenceData = await getReferenceData(group, 'sgo', value)
      console.log('SGO Reference Data:', {
        group,
        type: 'sgo',
        id: value,
        data: referenceData
      })

      // Remove existing fields
      const fieldCount = fields.length
      for (let i = fieldCount - 1; i >= 0; i--) {
        operation?.remove?.(i)
      }

      // Convert object to array and filter out length property
      const dataArray = Object.entries(referenceData)
        .filter(([key]) => key !== 'length')
        .map(([_, value]) => value)

      // Add reference data rows with only matching fields
      dataArray.forEach(row => {
        const newRow = {
          SG: row.sg,  // Leave blank as no matching field
          KRG: row.krg, // Leave blank as no matching field
          ...(!isCondensate && !isGas && { KRO: row.kro }),
          ...(row.psia !== undefined && { PSIA: row.psia })
        }
        operation?.add(newRow)
      })
    } catch (error) {
      console.error('Error loading reference data:', error)
    }
  }

  const fetchData = () => {
    setData(fields.map((field, index) => ({
      ...form.getFieldValue(["SCAL", tab, "SGO", field.name]),
      key: index
    })) ?? [])
  }

  useEffect(() => {
    validateData(data)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields])

  const columns = useMemo(
    () => {
      const baseColumns = [
        {
          key: 'key',
          dataIndex: 'key',
          title: "Row",
          editable: false,
          render: (dom, record, index) => index + 1
        },
        {
          key: 'SG',
          dataIndex: 'SG',
          title: 'Sg',
          editable: true,
        },
        {
          key: 'KRG',
          dataIndex: 'KRG',
          title: 'Krg',
          editable: true,
        }
      ]

      // Add KRO column only for BLACK_OIL type
      if (!isCondensate && !isGas) {
        baseColumns.push({
          key: 'KRO',
          dataIndex: 'KRO',
          title: 'Kro',
          editable: true,
        })
      }

      // Add PSIA column
      baseColumns.push({
        key: 'PSIA',
        dataIndex: 'PSIA',
        title: 'Pc (psia)',
        editable: true,
      })

      // Add operation column
      baseColumns.push({
        title: "Operation",
        valueType: "option",
        key: "operation",
        width: 160,
        render: (text, record, index, action) => [
          <Button
            key="editable"
            type="text"
            className="text-primary"
            onClick={() => {
              action?.startEditable?.(record.key);
            }}
          >
            Edit
          </Button>,
          <Popconfirm
            key="delete"
            title="Sure to delete?"
            onConfirm={() => {
              operation?.remove?.(index);
              const currentSGOData = form.getFieldValue(["SCAL", tab, "SGO"]);
              const newSGOData = currentSGOData.filter((_, i) => i !== index);
              form.setFieldValue(["SCAL", tab, "SGO"], newSGOData);
              fetchData();
            }}
          >
            <Button type="text" danger>Delete</Button>
          </Popconfirm>
        ],
      })

      return baseColumns
    }, [operation, isCondensate, isGas])

  const validateData = useCallback((_data = []) => {
    setErrors([])

    const _errors = []

    if (!_data.length) return;

    const sgValues = []
    const krwValues = []
    const krgValues = []
    const kroValues = []
    const pcValues = []

    _data.forEach((row, index) => {
      if (!isNumeric(row.SG)) {
        _errors.push(`Value of Sg in row #${index + 1} must be a number`)
      } else {
        const sg = parseFloat(row.SG)
        if (sg < 0 || sg > 1) {
          _errors.push(`Value of Sg in row #${index + 1} must be between 0 and 1`)
        } else sgValues.push(sg)
      }

      if (isGas) {
        if (!isNumeric(row.KRW)) {
          _errors.push(`Value of Krw in row #${index + 1} must be a number`)
        } else {
          const krw = parseFloat(row.KRW)
          if (krw < 0 || krw > 1) {
            _errors.push(`Value of Krw in row #${index + 1} must be between 0 and 1`)
          } else krwValues.push(krw)
        }
      } else {
        if (!isNumeric(row.KRG)) {
          _errors.push(`Value of Krg in row #${index + 1} must be a number`)
        } else {
          const krg = parseFloat(row.KRG)
          if (krg < 0 || krg > 1) {
            _errors.push(`Value of Krg in row #${index + 1} must be between 0 and 1`)
          } else krgValues.push(krg)
        }
      }

      if (!isCondensate && !isGas) {
        if (!isNumeric(row.KRO)) {
          _errors.push(`Value of Kro in row #${index + 1} must be a number`)
        } else {
          const kro = parseFloat(row.KRO)
          if (kro < 0 || kro > 1) {
            _errors.push(`Value of Kro in row #${index + 1} must be between 0 and 1`)
          } else kroValues.push(kro)
        }
      }

      if (!isNumeric(row.PSIA)) {
        _errors.push(`Value of Pc (psia) in row #${index + 1} must be a number`)
      } else {
        const pc = parseFloat(row.PSIA)
        pcValues.push(pc)
      }
    })

    if (_errors.length) {
      setErrors(_errors)
      return
    }

    const firstSgValue = sgValues[0]
    if (sgValues.length && firstSgValue !== 0) {
      _errors.push(`The first Sg value must be 0`)
    }

    if (isGas) {
      const isKrwDecreasing = isDecreasing(krwValues)
      if (!isKrwDecreasing) {
        _errors.push(`The values of Krw must be decreasing`)
      }
    } else {
      const firstKrgValue = krgValues[0]
      if (krgValues.length && firstKrgValue !== 0) {
        _errors.push(`The first Krg value must be 0`)
      }

      const isKrgIncreasing = isIncreasing(krgValues)
      if (!isKrgIncreasing) {
        _errors.push(`The values of Krg must be increasing`)
      }
    }

    if (!isCondensate && !isGas) {
      const lastKroValue = kroValues[kroValues.length - 1]
      if (kroValues.length > 1 && lastKroValue !== 0) {
        _errors.push(`The last Kro value must be 0`)
      }

      const isKroDecreasing = isDecreasing(kroValues)
      if (!isKroDecreasing) {
        _errors.push(`The values of Kro must be decreasing`)
      }
    }

    const isSgIncreasing = isIncreasing(sgValues)
    if (!isSgIncreasing) {
      _errors.push(`The values of Sg must be increasing`)
    }

    const isPcIncreasing = isIncreasing(pcValues)
    if (!isPcIncreasing) {
      _errors.push(`The values of Pc (psia) must be increasing`)
    }

    setErrors(_errors)
  }, [isCondensate, isGas])

  const handleFileUpload = (file) => {
    Papa.parse(file, {
      header: true,
      dynamicTyping: true,
      complete: (result) => {
        const parsedData = result.data
          .filter(row => Object.values(row).some(value => value !== null))
          .map((row, index) => ({
            ...row,
            key: generateUuid(),
          }));

        setData(parsedData);

        fields.forEach((field, index) => {
          if (parsedData[index]) {
            const recordData = {
              SG: parsedData[index].SG,
              ...(isGas 
                ? { KRW: parsedData[index].KRW }
                : { KRG: parsedData[index].KRG }
              ),
              ...(!isCondensate && !isGas && { KRO: parsedData[index].KRO }),
              PSIA: parsedData[index].PSIA
            };
            form.setFieldValue(["SCAL", tab, "SGO", field.name], recordData);
          }
        });

        validateData(parsedData);
      },
    });
    return false;
  };

  const handleSGOTableEditorOpen = () => {
    setEditingData(data);
    setIsSGOTableEditorOpen(true);
  };

  const handleSGOTableEditorCancel = () => {
    setEditingData(data);
    setIsSGOTableEditorOpen(false);
  };

  const handleSGOTableEditorSave = () => {
    const currentEditingData = tableEditorRef.current?.getCurrentData() || [];
    console.log("puppy",currentEditingData)
    // Remove existing fields
    const fieldCount = fields.length;
    for (let i = fieldCount - 1; i >= 0; i--) {
      operation?.remove?.(i);
    }

    // Add all rows from editing data
    currentEditingData.forEach((row) => {
      const newRow = {
        SG: row.SG,
        ...(isGas 
          ? { KRG: row.KRG }  // For GAS type
          : { KRG: row.KRG }  // For other types
        ),
        ...(!isCondensate && !isGas && { KRO: row.KRO }),
        PSIA: row.PSIA
      };
      operation?.add?.(newRow);
    });

    setData(currentEditingData);
    setIsSGOTableEditorOpen(false);
    fetchData();
  };

  // Update title based on fluid type
  const tableTitle = useMemo(() => {
    if (isGas || isCondensate) {
      return "SGF (Sg-Krg-Pc)"  // For Gas and Condensate types
    }
    return "SGO (Sg-Krg-Kro-Pc)"  // For Black Oil type
  }, [isGas, isCondensate])

  return (
    <FormSection
      title={tableTitle}  // Use dynamic title
      extra={
        <div className="d-flex justify-content-end align-items-center gap-2">
          <Button 
            icon={<TableOutlined />}
            onClick={handleSGOTableEditorOpen}
            title="Open Table Editor"
          />
          <Upload
            accept=".csv"
            showUploadList={false}
            beforeUpload={handleFileUpload}
          >
            <Button type="primary">Upload CSV</Button>
          </Upload>
          <Typography.Text className="text-light">Reference</Typography.Text>
          <Form.Item 
            name={['SCAL', tab, 'SGO', 'reference']}
            noStyle
          >
            <Select
              style={{ minWidth: 120 }} 
              dropdownMatchSelectWidth={false}
              options={referenceOptions}
              onChange={handleReferenceChange}
              allowClear
            />
          </Form.Item>
        </div>
      }
      bodyStyle={{
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 0
      }}
    >
      <EditableProTable
        options={false}
        pagination={false}
        search={false}
        columns={columns}
        value={data}
        rowKey="key"
        tableExtraRender={() => (
          <ul className="px-24 text-danger">
            {errors.map(errMsg => (
              <li key={errMsg}>
                <Typography.Text type="danger">{errMsg}</Typography.Text>
              </li>
            ))}
          </ul>
        )}
        sticky={{
          offsetHeader: 32
        }}
        className={classes.tableContainer}
        cardProps={{
          className: classes.card
        }}
        tableClassName={classes.table}
        editable={{
          type: 'multiple',
          actionRender: (row, config, defaultDom) => [
            defaultDom.save,
            defaultDom.cancel,
          ],
          onSave: (rowKey, data, row) => {
            const { index, isNew, ...recordData } = data
            if (isNew) {
              operation?.add?.(recordData)
            } else {
              form.setFieldValue(["SCAL", tab, "SGO", index], recordData)
              fetchData()
            }
          }
        }}
        recordCreatorProps={{
          record: {
            key: generateUuid(),
            isNew: true
          }
        }}
      />
      <Modal
        title="Table Editor"
        open={isSGOTableEditorOpen}
        onCancel={handleSGOTableEditorCancel}
        width={800}
        style={{ top: 20 }}
        bodyStyle={{ 
          padding: '12px',
          height: 'calc(80vh - 108px)',
          overflow: 'hidden'
        }}
        footer={[
          <Button key="cancel" onClick={handleSGOTableEditorCancel}>
            Cancel
          </Button>,
          <Button 
            key="save" 
            type="primary" 
            onClick={handleSGOTableEditorSave}
          >
            Save
          </Button>
        ]}
      >
        <div style={{ height: '100%' }}>
          <SGOTableEditor
            ref={tableEditorRef}
            data={editingData}
            columns={columns}
            onDataChange={setEditingData}
          />
        </div>
      </Modal>
    </FormSection>
  )
}

export default SGOTable